/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import appTheme from '../../common/theme/appTheme.json';
import styled from 'styled-components';
import { getFlightLegsInfo, updateFlightLegsOrder } from '../../../store/flightLegs/actions';
import { selectUserId, selectFlightLegsInfo, selectMissionPlanById, selectIsUpdatingTakeoffTime } from '../../../store';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Box, Table, TableBody, TableCell, TableRow, TableHead } from '@mui/material';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import DraggableLegContainer from './DraggableLegContainer';
import { FlightLegInfo } from '../../../shared/entities/flightLeg';
import WarningIcon from '@mui/icons-material/Warning';

const StyledTable = styled(Table)`
  table-layout: fixed;
`;

const StyledTableHeaderCell = {
  minWidth: '0px',
  fontSize: '14px',
  padding: '8px',
  fontFamily: appTheme.viasatFontFamilyRegular,
  borderBottomColor: appTheme.mptRowBorderBottom,
  backgroundColor: appTheme.mptPrimaryBackgroundColor,
  textAlign: 'center',
  fontWeight: 400,
  color: appTheme.mptFormLabelText,
};

const StyledWarningBox = styled(Box)`
  display: flex;
  align-items: center;
  color: white;
`;

const HiddenWarningIcon = styled(WarningIcon)`
  color: ${appTheme.mptPrimaryBackgroundColor};
  font-size: small;
`;

export interface FlightLegsTableProps {}

const FlightLegsTable = (props: FlightLegsTableProps) => {
  const dispatch = useDispatch();
  const savedMissionPlan = useSelector(selectMissionPlanById, shallowEqual);
  const userId = useSelector(selectUserId, shallowEqual);
  const flightLegs = useSelector(selectFlightLegsInfo, shallowEqual);
  const isUpdatingTakeoffTime = useSelector(selectIsUpdatingTakeoffTime, shallowEqual);
  const [legOrderList, setLegOrderList] = useState<string[]>([]);
  const [showWarning, setShowWarning] = useState<boolean>(false);

  // Compare two arrays
  const compare = (list1: string[], list2: string[]): boolean => {
    let equal = true;

    if (list1.length !== list2.length) {
      return false;
    }

    list1.forEach((elem: string, index: number) => {
      if (elem !== list2[index]) {
        equal = false;
      }
    });

    return equal;
  };

  // Update list of flight leg ids by leg order
  useEffect(() => {
    let newOrderList: string[] = [];
    flightLegs.forEach((flightLeg: FlightLegInfo, index: number) => {
      newOrderList.push(flightLeg.id.toString());
    });

    const isEqual = compare(newOrderList, legOrderList);

    setLegOrderList(newOrderList);

    if (savedMissionPlan && savedMissionPlan.id && userId && newOrderList.length && !isEqual) {
      dispatch(updateFlightLegsOrder(savedMissionPlan.id, userId, newOrderList));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightLegs, dispatch]);

  // Show/Hide warning message above flight legs table
  useEffect(() => {
    if (flightLegs.length) {
      let warnUser = false;
      flightLegs.forEach((flightLeg: FlightLegInfo, index: number) => {
        const prevIndex = index - 1;
        if (flightLegs[prevIndex] && flightLegs[prevIndex].destination !== flightLegs[index].origin) {
          warnUser = true;
        }
      });
      setShowWarning(warnUser);
    }
  }, [flightLegs]);

  // Show/Hide warning message above flight legs table
  useEffect(() => {
    if (flightLegs.length) {
      let warnUser = false;
      flightLegs.forEach((flightLeg: FlightLegInfo, index: number) => {
        const prevIndex = index - 1;
        if (flightLegs[prevIndex] && flightLegs[prevIndex].destination !== flightLegs[index].origin) {
          warnUser = true;
        }
      });
      setShowWarning(warnUser);
    }
  }, [flightLegs]);

  // GET /flightlegs/infos on table mount
  useEffect(() => {
    if (savedMissionPlan && savedMissionPlan.id && userId) {
      dispatch(getFlightLegsInfo(savedMissionPlan.id, userId));
    }
  }, [dispatch, savedMissionPlan, userId, isUpdatingTakeoffTime]);

  const onDragEnd = useCallback(
    result => {
      const { source, destination, draggableId } = result;

      // Edge cases; no work is required
      if (!destination) {
        return;
      } else if (destination.index === source.index) {
        return;
      }

      // Update leg ordering (redux)
      let listArr: string[] = legOrderList;
      listArr.splice(source.index, 1);
      listArr.splice(destination.index, 0, draggableId);

      setLegOrderList(listArr);

      if (savedMissionPlan && savedMissionPlan.id && userId && listArr && listArr.length) {
        dispatch(updateFlightLegsOrder(savedMissionPlan.id, userId, listArr));
      }
    },
    [legOrderList, savedMissionPlan, userId, dispatch],
  );

  return (
    <React.Fragment>
      {showWarning ? (
        <StyledWarningBox>
          <WarningIcon fontSize="small" /> Warning: Departure airport does not match previous arrival airport.
        </StyledWarningBox>
      ) : null}
      <StyledTable aria-label="Flight Legs Table">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...StyledTableHeaderCell, width: '30px' }}>
              <HiddenWarningIcon />
            </TableCell>
            <TableCell sx={{ ...StyledTableHeaderCell, width: '25px' }}>Leg</TableCell>
            <TableCell sx={{ ...StyledTableHeaderCell, width: '80px' }}>Departure</TableCell>
            <TableCell sx={{ ...StyledTableHeaderCell, width: '84px' }}>Takeoff</TableCell>
            <TableCell sx={{ ...StyledTableHeaderCell, width: '70px' }}>Duration</TableCell>
            <TableCell sx={{ ...StyledTableHeaderCell, width: '55px' }}>Arrival</TableCell>
            <TableCell sx={{ ...StyledTableHeaderCell, width: '80px' }}>Waypoints</TableCell>
            <TableCell sx={{ ...StyledTableHeaderCell, width: '100px' }}>Aircraft Type</TableCell>
            <TableCell sx={{ ...StyledTableHeaderCell, width: '30px' }}>
              <HiddenWarningIcon />
            </TableCell>
          </TableRow>
        </TableHead>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={'flightLegTable'}>
            {(dropProvided: any, dropSnapshot: any) => (
              <TableBody ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                {flightLegs.map((flightLeg: FlightLegInfo, index: number) => {
                  const prevIndex = index - 1;
                  let warnUser = false;

                  if (flightLegs[prevIndex] && flightLegs[prevIndex].destination !== flightLegs[index].origin) {
                    warnUser = true;
                  }
                  return (
                    <DraggableLegContainer
                      flightLeg={flightLeg}
                      missionPlanId={savedMissionPlan.id}
                      index={index}
                      key={flightLeg.id}
                      warning={warnUser}
                    />
                  );
                })}
                {dropProvided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
      </StyledTable>
    </React.Fragment>
  );
};

export default FlightLegsTable;
